/**
 * Telemetry client (010 US2, FR-004/005/006).
 *
 * - Heartbeat (~5s while active).
 * - Event batches (job/item/export/trace) → POST /api/telemetry/events.
 * - Offline buffer in PlatformIO.localStore (SQLite/IndexedDB); flush on reconnect
 *   with NO loss and correct client timings; never blocks rendering.
 * - METADATA ONLY: every event passes the pixel/secret guard before buffer/send.
 */
import type {
  HeartbeatPayload,
  JsonClient,
  LocalStore,
  PlatformIO,
  TelemetryEvent,
} from "@genz/platform";
import { assertNoPixels } from "@genz/platform";

const BUFFER_COLLECTION = "telemetry.buffer";
/** Buffer cap + prune cadence (see emit) — the buffer was previously unbounded. */
const MAX_BUFFERED = 5_000;
const PRUNE_EVERY = 200;
/** F-9 — the heartbeat backoff cap, in ticks (5 s each ⇒ ~2.7 min at the top). */
const HEARTBEAT_MAX_SKIP = 32;

/**
 * F-3 — the 403 details that mean "no retry will ever work", by OUR SERVER'S EXACT WORDS.
 *
 * A 403 is not one event, and the client only gets to treat one as permanent when it can tell
 * that our own application refused it. Two things make that decision safe:
 *
 *  1. The body must be REAL JSON. `readErrorBody` (`packages/platform/src/jsonClient.ts`) parses
 *     a JSON body into its own shape and wraps anything else as `{ detail: <text>, contentType }`
 *     — so the presence of `contentType` is exactly "this was not JSON". A stock Cloudflare or
 *     Apache 403 PAGE says "You do not have permission…" and "Your role…", which a substring
 *     rule read as our server naming a permanent refusal; the first review of this wave shipped
 *     that, and it would stop liveness for the whole session on an edge fault that clears by
 *     itself.
 *  2. The detail must be one of the strings our cloud actually sends:
 *       - `cloud/app/telemetry/router.py` — the heartbeat's own device-ownership refusal;
 *       - `cloud/app/core/deps.py` `require_role` → `forbidden("Insufficient role")`.
 *     Matched whole and case-insensitively, not as a substring of prose.
 *
 * Anything else — an unrecognised JSON detail, an HTML page, an empty body — backs off.
 */
const PERMANENT_403_DETAILS = [
  "device_id belongs to another customer",
  "insufficient role",
];

/** Whether a 403's body is OUR server naming a refusal that no retry can clear. */
function isPermanentForbidden(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  // `readErrorBody` only sets `contentType` on the NON-JSON wrapper, so its presence means the
  // server (or something in front of it) answered with a page, not with our API's error shape.
  if ("contentType" in (body as Record<string, unknown>)) return false;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail !== "string") return false;
  const normalised = detail.trim().toLowerCase();
  return PERMANENT_403_DETAILS.includes(normalised);
}

export interface TelemetryClientOptions {
  client: JsonClient;
  io: PlatformIO;
  deviceId: string;
  tenantId: string;
  appVersion: string;
  engineVersion: string;
  heartbeatMs?: number;
  /** Max events per flush batch (backpressure). */
  batchSize?: number;
  /**
   * F-9 — where a heartbeat that STOPS is reported. The heartbeat is liveness telemetry with
   * no operator-facing surface, so "visible" here means one structured log line, once, with a
   * step name — not a banner and not 720 console errors an hour.
   */
  logger?: { log(level: "info" | "warn" | "error", message: string, ctx?: unknown): void };
}

interface BufferedEvent {
  bufferId: string;
  event: TelemetryEvent;
  /**
   * WS-ERR H-2 — the identity this event was captured under, or `undefined` for one captured
   * before anyone signed in.
   *
   * The server attributes an event to whoever is AUTHENTICATED when it arrives. `start()` ends
   * with a flush and `bootstrap.ts` calls `start()` on the session-gained edge, so without this
   * account A's buffered events left under account B's token the moment B signed in — A's
   * activity filed under B's tenant. A tag on the row is the guard that does not depend on any
   * sign-out path having run.
   */
  identity?: string;
}

export class TelemetryClient {
  private readonly client: JsonClient;
  private readonly io: PlatformIO;
  private readonly store: LocalStore;
  private readonly opts: TelemetryClientOptions;
  private heartbeatTimer?: ReturnType<typeof setInterval>;
  private activeJobId?: string;
  private progress?: number;
  private flushing = false;
  private unsub?: () => void;
  private seq = 0;
  /**
   * F-9 — the heartbeat 403 loop. `POST /api/telemetry/heartbeat` answers 403 when this
   * `deviceId` already belongs to ANOTHER customer (`cloud/app/telemetry/ingest.py` —
   * `HeartbeatForbidden`), which is exactly what a demo machine does when it signs in as the
   * admin and then as a customer. The old `catch {}` called that "transient", so the client
   * re-POSTed every 5 s forever: 720 failed requests an hour, a console full of red, and no
   * line anywhere saying what was wrong. 403 is PERMANENT for this device/account pair — no
   * number of retries can change who owns the row — so the loop STOPS and says so once.
   */
  private heartbeatBlocked = false;
  /** Consecutive transient heartbeat failures, and the ticks to skip before the next try.
   *  A flat 5 s retry against a down server is the same amplifying loop at a lower volume. */
  private heartbeatFailures = 0;
  private heartbeatSkip = 0;
  /** F-3: the cap has been reported. `heartbeatSkip` STICKS at `MAX - 1` once the backoff is
   *  saturated, so "log when we reach the cap" fired on every tick from then on — the
   *  amplifier the backoff exists to stop, moved from requests to log lines. */
  private heartbeatCapReported = false;
  /** H-2 — whose events we are buffering and sending now. `null` before anyone signs in. */
  private identity: string | null = null;
  /** The local store cannot hold the buffer. Reported once; telemetry then no-ops. */
  private bufferWriteFailed = false;

  constructor(opts: TelemetryClientOptions) {
    this.opts = opts;
    this.client = opts.client;
    this.io = opts.io;
    this.store = opts.io.localStore;
  }

  /**
   * H-2 — whose session this is. Set by `app/bootstrap.ts` on the session edge, BEFORE
   * `start()`, so the flush at the end of `start()` can already tell A's events from B's.
   * `null` on sign-out: nothing is attributable until someone signs in again.
   */
  setIdentity(identity: string | null): void {
    this.identity = identity;
  }

  /** Whose events this client is buffering and sending. Read by the boot-order test that pins
   *  the seed in `app/bootstrap.ts`; nothing in the app depends on it. */
  get currentIdentity(): string | null {
    return this.identity;
  }

  start(): void {
    // F-3: a new session on this machine gets a working heartbeat. The stop is per
    // (device, account) pair, and `start()` is the edge where the account can have changed —
    // without this, one customer's 403 silenced liveness for every later sign-in until the
    // app was restarted.
    this.heartbeatBlocked = false;
    this.heartbeatFailures = 0;
    this.heartbeatSkip = 0;
    this.heartbeatCapReported = false;
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    const interval = this.opts.heartbeatMs ?? 5000;
    this.heartbeatTimer = setInterval(() => void this.sendHeartbeat(), interval);
    // M-1: release the PREVIOUS subscription first. `start()` is called again on every session
    // edge (and a token rotation re-runs that edge), and overwriting `this.unsub` left the old
    // listener attached with nothing holding its remover — one extra flush per reconnect, per
    // rotation, for the life of the process.
    this.unsub?.();
    this.unsub = this.io.onConnectivityChange((online) => {
      if (online) void this.flush();
    });
    // Attempt an immediate flush of anything buffered from a previous session.
    void this.flush();
  }

  stop(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = undefined;
    // F-3: a tick already scheduled when `stop()` lands must not fire a request into a session
    // that is over. `start()` clears this again.
    this.heartbeatBlocked = true;
    this.unsub?.();
  }

  /** F-9 — whether the heartbeat loop has stopped itself on a permanent refusal. Read by the
   *  test that pins "a 403 stops the loop"; nothing in the UI depends on it. */
  get heartbeatStopped(): boolean {
    return this.heartbeatBlocked;
  }

  setActive(jobId: string | undefined, progress?: number): void {
    this.activeJobId = jobId;
    this.progress = progress;
  }

  /** Enqueue a telemetry event — guarded, buffered, then flushed if online. */
  async emit(event: TelemetryEvent): Promise<void> {
    // Pixel/secret guard (FR-006, SC-004) — throws before any buffering/send.
    assertNoPixels(event);
    const bufferId = `${Date.now().toString(36)}-${this.seq++}`;
    try {
      await this.store.records.put(BUFFER_COLLECTION, bufferId, {
        bufferId, event,
        // H-2: stamped at CAPTURE time. An event captured before anyone signed in carries no
        // identity and is sendable by whoever signs in first — that is AUD-4 IMP-5's pre-login
        // failure, which belongs to nobody yet and is the one most worth having.
        ...(this.identity ? { identity: this.identity } : {}),
      } satisfies BufferedEvent);
    } catch (e) {
      // WS-ERR: every caller is `void telemetry.emit(...)` (`app/bootstrap.ts`'s `onChange`
      // fires one per job per tick), so a local store that cannot write turned EVERY event
      // into an unhandled rejection — a console full of them during a render run, on a device
      // that was otherwise working. Telemetry is the least important thing on this machine:
      // it degrades, it does not shout. Said ONCE, with the reason.
      if (!this.bufferWriteFailed) {
        this.bufferWriteFailed = true;
        this.opts.logger?.log("warn", "Telemetry · this device cannot buffer events — telemetry is off for this session", {
          step: "emit", error: e instanceof Error ? e.message : String(e),
        });
      }
      return;
    }
    // The buffer had NO cap: a long offline queue session (onChange emits one job event per
    // job per tick) grew it unboundedly. Prune the oldest beyond MAX_BUFFERED, checked every
    // PRUNE_EVERY emits so the full scan stays off the hot path.
    if (this.seq % PRUNE_EVERY === 0) {
      const all = await this.store.records.all<BufferedEvent>(BUFFER_COLLECTION).catch(() => []);
      if (all.length > MAX_BUFFERED) {
        all.sort((a, b) => a.bufferId.localeCompare(b.bufferId));
        for (const old of all.slice(0, all.length - MAX_BUFFERED)) {
          await this.store.records.remove(BUFFER_COLLECTION, old.bufferId).catch(() => {});
        }
      }
    }
    if (this.io.isOnline()) void this.flush();
  }

  /** Only telemeter when authenticated: a real login sets a client token. Without
   * one, authed heartbeats/flushes would just 401 every tick — wasteful and, on
   * older clients, enough to bounce the session back to login. */
  private authenticated(): boolean {
    return !!this.client.getTokens()?.access_token;
  }

  private async sendHeartbeat(): Promise<void> {
    if (!this.authenticated()) return;
    // F-9: a refused device/account pairing is permanent — the timer is already stopped, this
    // guard only covers a tick that was already scheduled when the refusal landed.
    if (this.heartbeatBlocked) return;
    if (this.heartbeatSkip > 0) { this.heartbeatSkip -= 1; return; }
    const payload: HeartbeatPayload = {
      deviceId: this.opts.deviceId,
      tenantId: this.opts.tenantId,
      online: this.io.isOnline(),
      activeJobId: this.activeJobId,
      progress: this.progress,
      appVersion: this.opts.appVersion,
      engineVersion: this.opts.engineVersion,
      ts: Date.now(),
    };
    assertNoPixels(payload);
    if (!this.io.isOnline()) return; // heartbeat is liveness; buffering it is pointless
    try {
      await this.client.heartbeat(payload);
      this.heartbeatFailures = 0;
      this.heartbeatSkip = 0;
      this.heartbeatCapReported = false;
    } catch (e) {
      const status = (e as { status?: unknown })?.status;
      // F-3: only a 403 the SERVER EXPLAINS is permanent. `cloud/app/telemetry/router.py`
      // names the cause ("device_id belongs to another customer"); a WAF or proxy 403 names
      // nothing and clears on its own, and stopping the session's liveness on one of those —
      // with no way back — is a worse outcome than the loop it was meant to stop.
      if (status === 403 && isPermanentForbidden((e as { body?: unknown })?.body)) {
        this.heartbeatBlocked = true;
        if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = undefined;
        this.opts.logger?.log(
          "warn",
          "Telemetry · heartbeat refused (403) — this device id belongs to another account. Liveness reporting is off for this session.",
          { step: "heartbeat", deviceId: this.opts.deviceId, status: 403 },
        );
        return;
      }
      // Everything else (offline, 5xx, timeout, an unexplained 403): back off 1, 2, 4, 8 …
      // ticks, capped, instead of hammering every tick. JITTERED, so a fleet that went down
      // together does not come back on the same instant together.
      this.heartbeatFailures += 1;
      const window = Math.min(2 ** this.heartbeatFailures, HEARTBEAT_MAX_SKIP);
      this.heartbeatSkip = Math.max(0, Math.round(window * (0.5 + Math.random() * 0.5)) - 1);
      // One line on the FIRST failure and one when the backoff saturates — never one per tick
      // (release-audit-checklist, "A recurring failure logs ONCE").
      const atCap = window >= HEARTBEAT_MAX_SKIP;
      if (this.heartbeatFailures === 1 || (atCap && !this.heartbeatCapReported)) {
        if (atCap) this.heartbeatCapReported = true;
        this.opts.logger?.log("info", "Telemetry · heartbeat failing, backing off", {
          step: "heartbeat", failures: this.heartbeatFailures, skipTicks: this.heartbeatSkip,
          status: typeof status === "number" ? status : undefined,
        });
      }
    }
  }

  /** Flush buffered events in batches; survives partial failure (no loss). */
  async flush(): Promise<void> {
    // Buffer (never lose) but don't POST while unauthenticated — without a client
    // token a flush would just 401. Events stay queued and flush after login.
    //
    // `isOnline()` is deliberately NOT a gate here (see ClientLogger.flush for the full
    // reasoning): it is `navigator.onLine`, which a Tauri WebView reports unreliably, and one
    // wrong value used to stop telemetry delivery permanently because the periodic retry
    // no-op'd as well. Let the request itself decide; a failure keeps the buffer.
    if (this.flushing || !this.authenticated()) return;
    this.flushing = true;
    try {
      const batchSize = this.opts.batchSize ?? 50;
      let buffered = await this.readSendable();
      while (buffered.length > 0) {
        const slice = buffered.slice(0, batchSize);
        try {
          await this.client.sendEvents(slice.map((b) => b.event));
        } catch (e) {
          // Transient (network/5xx/auth) → keep the buffer and retry later. A PERMANENT
          // rejection (413/422/…) must be dead-lettered — the same head-of-buffer batch
          // otherwise re-sends forever and stalls the whole queue behind it.
          const status = (e as { status?: unknown })?.status;
          const permanent = typeof status === "number" && status >= 400 && status < 500
            && status !== 401 && status !== 403 && status !== 408 && status !== 429;
          if (!permanent) break; // keep buffer intact; retry on next reconnect/heartbeat
          for (const b of slice) await this.store.records.remove(BUFFER_COLLECTION, b.bufferId);
          buffered = await this.readSendable();
          continue;
        }
        for (const b of slice) await this.store.records.remove(BUFFER_COLLECTION, b.bufferId);
        buffered = await this.readSendable();
      }
    } finally {
      this.flushing = false;
    }
  }

  /**
   * H-2 — the buffered events THIS identity may send, with any foreign ones removed on the way.
   *
   * A row tagged with another account is not held back for later: the account that owns it is
   * gone from this device, so there is no future in which sending it is correct, and leaving it
   * in the buffer is a leak waiting for the next flush. An UNTAGGED row (captured before anyone
   * signed in) is sendable by whoever signs in first.
   */
  private async readSendable(): Promise<BufferedEvent[]> {
    const all = await this.store.records.all<BufferedEvent>(BUFFER_COLLECTION);
    // WS-ERR — a TAGGED row is never judged while nobody is signed in.
    //
    // `app/bootstrap.ts` calls `start()` once during boot and `start()`'s tail is a flush, so
    // the app's FIRST flush of every launch runs before `syncTicker` has learned who is signed
    // in. Reading "tagged, and not mine" as foreign THERE deleted last run's offline events —
    // the same account's — on every single launch, which is the exact opposite of what this
    // buffer is for. With no identity there is nothing to compare against, so a tagged row is
    // DEFERRED, not dropped: the identity that arrives a moment later decides.
    //
    // An UNTAGGED row is still sendable here. It was captured before anyone signed in (AUD-4
    // IMP-5's pre-login failure), it belongs to nobody, and `authenticated()` already gates the
    // flush on there being a token at all.
    const anonymous = this.identity === null;
    const mine: BufferedEvent[] = [];
    let foreign = 0;
    for (const b of all) {
      if (b.identity && anonymous) continue;                  // defer — decided once we know
      if (b.identity && b.identity !== this.identity) {
        foreign += 1;
        await this.store.records.remove(BUFFER_COLLECTION, b.bufferId).catch(() => {});
        continue;
      }
      mine.push(b);
    }
    if (foreign > 0) {
      this.opts.logger?.log("info", "Telemetry · dropped buffered events belonging to a previous account", {
        step: "flush", dropped: foreign,
      });
    }
    return mine;
  }

  /** Drop every buffered event WITHOUT sending (sign-out). Events captured under the
   *  previous account must never flush under the next account's token — the server
   *  attributes them to whoever is authenticated at flush time. */
  async purgeBuffer(): Promise<void> {
    await this.store.records.clear(BUFFER_COLLECTION).catch(() => {});
  }

  /** Count of un-synced events (UI: "offline — N pending"). */
  async pendingCount(): Promise<number> {
    return (await this.store.records.all(BUFFER_COLLECTION)).length;
  }
}
